/**
 * PatientImportSessionPage Component
 *
 * Status-driven session page for the patient import wizard.
 * Renders the appropriate step component based on the current session status.
 */

import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSubjectLanguage } from '@features/user';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { capitalize } from '@utils/utils';

import { usePatientImportSession } from '../hooks';
import { useGetReviewSummaryQuery } from '../store/hooks';
import type { ImportSessionStatus } from '../types';

import { ImportCompleteSummary } from './ImportCompleteSummary';
import { ImportProgressStep } from './ImportProgressStep';
import { PatientImportErrorBoundary } from './PatientImportErrorBoundary';
import { ImportReviewWizard } from './review-wizard';

// =============================================================================
// Helpers
// =============================================================================

const STEP_TITLES: Partial<Record<ImportSessionStatus, string>> = {
  pending: 'Analyzing',
  processing: 'Processing',
  in_progress: 'Processing',
  extracting: 'Processing',
  matching: 'Matching',
  ready_for_review: 'Review Records',
  importing: 'Importing',
  completed: 'Import Complete',
  failed: 'Import Failed',
  cancelled: 'Cancelled',
};

function getStepTitle(
  status: ImportSessionStatus | undefined,
  subjectLanguage: string,
): string {
  if (!status) return `Import ${capitalize(subjectLanguage)}s`;
  return STEP_TITLES[status] ?? `Import ${capitalize(subjectLanguage)}s`;
}

// =============================================================================
// Inner component (requires sessionId)
// =============================================================================

interface SessionContentProps {
  sessionId: string;
}

const SessionContent: React.FC<SessionContentProps> = ({ sessionId }) => {
  const navigate = useNavigate();
  const subjectLanguage = useSubjectLanguage();

  const {
    status,
    session,
    cancelSession,
    isCancelling,
  } = usePatientImportSession(sessionId);

  // Read cached review summary — survives wizard unmount, powers completion breakdown
  const { data: reviewSummary } = useGetReviewSummaryQuery(
    { sessionId },
    { skip: status !== 'completed' },
  );

  // ---------------------------------------------------------------------------
  // Render the correct step based on status
  // ---------------------------------------------------------------------------

  const renderStep = () => {
    switch (status) {
      case 'pending':
      case 'processing':
      case 'in_progress':
      case 'extracting':
        return (
          <ImportProgressStep
            message="Analyzing your file..."
            onCancel={cancelSession}
            isCancelling={isCancelling}
          />
        );

      case 'matching':
        return (
          <ImportProgressStep
            message={`Matching records to existing ${subjectLanguage}s...`}
            onCancel={cancelSession}
            isCancelling={isCancelling}
          />
        );

      case 'ready_for_review':
        return (
          <ImportReviewWizard
            sessionId={sessionId}
            onCancel={cancelSession}
          />
        );

      case 'importing': {
        const progress = session?.progressPercentage ?? 0;
        const processed = session?.processedPatients ?? 0;
        const total = session?.totalPatients ?? 0;
        const isFinishing = progress >= 100;
        return (
          <ImportProgressStep
            message={isFinishing ? 'Finishing up...' : `Importing ${subjectLanguage}s...`}
            progressPercentage={isFinishing ? undefined : progress}
            progressLabel={!isFinishing && total > 0 ? `${processed} of ${total} records` : undefined}
          />
        );
      }

      case 'completed': {
        const failed = session?.failedPatients ?? 0;
        return (
          <ImportCompleteSummary
            newPatientsCount={reviewSummary?.new_patients ?? 0}
            matchedPatientsCount={reviewSummary?.matched_patients ?? 0}
            appointmentsCount={reviewSummary?.appointments ?? 0}
            failedCount={failed}
            errors={session?.errorDetails}
            sessionId={sessionId}
          />
        );
      }

      case 'failed':
        return (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              {session?.errorMessages && session.errorMessages.length > 0 ? (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {session.errorMessages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </Box>
              ) : (
                `The import session failed. Please try uploading your file again.`
              )}
            </Alert>
            <Button
              variant="outlined"
              onClick={() => cancelSession()}
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling...' : 'Back to Import'}
            </Button>
          </Box>
        );

      case undefined:
        // Still loading
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 10,
            }}
          >
            <CircularProgress />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        maxWidth: status === 'ready_for_review' ? 'none' : 1000,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <IconButton
          onClick={() => navigate('/patients/import')}
          aria-label="Back to import sessions"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {getStepTitle(status, subjectLanguage)}
        </Typography>
      </Box>

      {/* Step content */}
      {renderStep()}
    </Box>
  );
};

// =============================================================================
// Page component (handles missing param)
// =============================================================================

export const PatientImportSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();

  if (!sessionId) {
    return <Navigate to="/patients/import" replace />;
  }

  return (
    <PatientImportErrorBoundary
      componentName="PatientImportSessionPage"
      sessionId={sessionId}
    >
      <SessionContent sessionId={sessionId} />
    </PatientImportErrorBoundary>
  );
};
